import { useParams } from 'react-router-dom'

import { useStore } from '@state/store.ts'
import { type StoreGrant, type StoreRole, type SystemRole, useCurrentUser } from '@state/user.ts'

const EFFECTIVE_STORE_ROLE: Partial<Record<SystemRole, StoreRole>> = {
  ADMIN: 'STORE_MANAGER',
  DEVELOPER: 'STORE_EMPLOYEE',
  CONTRIBUTOR: 'STORE_EMPLOYEE',
  VIEWER: 'STORE_VIEWER',
}

export function hasAccessToAllStores(role: SystemRole) {
  return role in EFFECTIVE_STORE_ROLE
}

const STORE_ROLE_RANK: StoreRole[] = ['STORE_VIEWER', 'STORE_EMPLOYEE', 'STORE_MANAGER']

/**
 * The higher of the explicit grant for the store and the global role's store role,
 * mirroring the backend, which ORs global and store-level permissions.
 */
export function getEffectiveStoreRole(systemRole: SystemRole, storeGrants: StoreGrant[], storeId: string): StoreRole | undefined {
  const candidates = [
    storeGrants.find(grant => grant.store.id === storeId)?.role,
    EFFECTIVE_STORE_ROLE[systemRole],
  ].filter(role => role !== undefined)

  return candidates.sort((a, b) => STORE_ROLE_RANK.indexOf(b) - STORE_ROLE_RANK.indexOf(a))[0]
}

/**
 * Hook to get the current store from the URL.
 * URL is the single source of truth for the currently selected store.
 *
 * @throws Error if no store is selected or store doesn't exist
 */
export function useCurrentStore() {
  const { storeId: selectedStoreId } = useParams<{ storeId?: string }>()
  const { role, storeGrants } = useCurrentUser()
  const { data: store } = useStore(selectedStoreId)

  if (!store) {
    throw new Error(`Invalid Store selected: ${selectedStoreId}. Ensure this hook is wrapped in a StoreSelectGuard and store exists in user data.`)
  }

  const effectiveRole = getEffectiveStoreRole(role, storeGrants, store.id)

  if (!effectiveRole) {
    throw new Error(`User does not have access to Store: ${store.id}. Ensure this hook is wrapped in a StoreSelectGuard and store exists in user data.`)
  }

  return { ...store, role: effectiveRole }
}

export function useCurrentStoreId() {
  const { id: selectedStoreId } = useCurrentStore()
  return selectedStoreId
}